<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import analyticsAPI from 'dashboard/api/pipeline/analytics';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  startDate: {
    type: Date,
    required: true,
  },
  endDate: {
    type: Date,
    required: true,
  },
  pipelineId: {
    type: [String, Number],
    default: null,
  },
});

const { t } = useI18n();

const loading = ref(false);
const pipelineData = ref(null);

const loadPipelineAnalysis = async () => {
  if (!props.pipelineId) return;

  loading.value = true;
  try {
    const response = await analyticsAPI.getPipelineAnalysis(
      props.pipelineId,
      props.startDate.toISOString(),
      props.endDate.toISOString()
    );
    pipelineData.value = response.data;
  } catch {
    pipelineData.value = null;
  } finally {
    loading.value = false;
  }
};

const getSeverityColor = severity => {
  const colors = {
    critical: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6',
    high: 'bg-n-amber-3 text-n-amber-11 border-n-amber-6',
    medium: 'bg-n-blue-3 text-n-blue-11 border-n-blue-6',
    low: 'bg-n-alpha-2 text-n-slate-11 border-n-weak',
  };
  return colors[severity] || colors.low;
};

const getFlowColor = netFlow => {
  if (netFlow > 0) return 'text-n-teal-11';
  if (netFlow < 0) return 'text-n-ruby-11';
  return 'text-n-slate-11';
};

onMounted(() => {
  loadPipelineAnalysis();
});

watch(
  [() => props.startDate, () => props.endDate, () => props.pipelineId],
  () => {
    loadPipelineAnalysis();
  }
);
</script>

<template>
  <div class="rounded-lg border border-n-weak bg-n-solid-2 p-5">
    <div class="flex items-center justify-between mb-5">
      <div>
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('PIPELINE.ANALYTICS.PIPELINE_ANALYSIS.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11 mt-0.5">
          {{ t('PIPELINE.ANALYTICS.PIPELINE_ANALYSIS.SUBTITLE') }}
        </p>
      </div>
      <Icon icon="i-lucide-git-branch" class="size-5 text-n-slate-9" />
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12">
      <div
        class="animate-spin rounded-full h-6 w-6 border-2 border-n-weak border-t-n-brand"
      />
    </div>

    <div v-else-if="pipelineData" class="space-y-5">
      <!-- Stage Flow -->
      <div>
        <h4 class="text-xs font-medium text-n-slate-11 mb-3">
          {{ t('PIPELINE.ANALYTICS.PIPELINE_ANALYSIS.STAGE_FLOW') }}
        </h4>
        <div class="space-y-2">
          <div
            v-for="flow in pipelineData.stage_flow"
            :key="`${flow.from_stage}-${flow.to_stage}`"
            class="flex items-center justify-between p-3 bg-n-alpha-2 rounded-lg"
          >
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ flow.from_stage }}
              </span>
              <Icon icon="i-lucide-arrow-right" class="size-4 text-n-slate-9" />
              <span class="text-sm font-medium text-n-slate-12">
                {{ flow.to_stage }}
              </span>
            </div>
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-1">
                <Icon
                  icon="i-lucide-arrow-up"
                  class="size-3.5 text-n-teal-11"
                />
                <span class="text-sm text-n-slate-11">
                  {{ flow.forward_movements }}
                </span>
              </div>
              <div class="flex items-center gap-1">
                <Icon
                  icon="i-lucide-arrow-down"
                  class="size-3.5 text-n-ruby-11"
                />
                <span class="text-sm text-n-slate-11">
                  {{ flow.backward_movements }}
                </span>
              </div>
              <span
                class="text-sm font-medium"
                :class="getFlowColor(flow.net_flow)"
              >
                {{ flow.net_flow > 0 ? '+' : '' }}{{ flow.net_flow }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Average Time per Stage -->
      <div>
        <h4 class="text-xs font-medium text-n-slate-11 mb-3">
          {{ t('PIPELINE.ANALYTICS.PIPELINE_ANALYSIS.AVERAGE_TIME') }}
        </h4>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <div
            v-for="(time, stage) in pipelineData.average_times"
            :key="stage"
            class="p-3 bg-n-alpha-2 rounded-lg"
          >
            <div class="text-[10px] text-n-slate-11 mb-1 truncate">
              {{ stage }}
            </div>
            <div class="text-base font-semibold text-n-slate-12">
              {{ (time / 86400).toFixed(1) }}
              <span class="text-xs font-normal text-n-slate-11">
                {{ t('PIPELINE.ANALYTICS.PIPELINE_ANALYSIS.DAYS') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottlenecks -->
      <div v-if="pipelineData.bottlenecks.length > 0">
        <h4 class="text-xs font-medium text-n-slate-11 mb-3">
          {{ t('PIPELINE.ANALYTICS.PIPELINE_ANALYSIS.BOTTLENECKS') }}
        </h4>
        <div class="space-y-2">
          <div
            v-for="bottleneck in pipelineData.bottlenecks"
            :key="bottleneck.stage"
            class="flex items-center justify-between p-3 border rounded-lg"
            :class="getSeverityColor(bottleneck.severity)"
          >
            <div class="flex items-center gap-3">
              <Icon icon="i-lucide-alert-triangle" class="size-4" />
              <div>
                <div class="text-sm font-medium">
                  {{ bottleneck.stage_name }}
                </div>
                <div class="text-[10px] opacity-75 capitalize">
                  {{
                    t(
                      `PIPELINE.ANALYTICS.PIPELINE_ANALYSIS.SEVERITY.${bottleneck.severity.toUpperCase()}`
                    )
                  }}
                </div>
              </div>
            </div>
            <div class="text-right">
              <div class="text-base font-semibold">
                {{ bottleneck.average_time_days }}
              </div>
              <div class="text-[10px] opacity-75">
                {{ t('PIPELINE.ANALYTICS.PIPELINE_ANALYSIS.DAYS') }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-8">
        <Icon
          icon="i-lucide-check-circle"
          class="size-10 mx-auto mb-2 text-n-teal-9"
        />
        <p class="text-sm text-n-slate-11">
          {{ t('PIPELINE.ANALYTICS.PIPELINE_ANALYSIS.NO_BOTTLENECKS') }}
        </p>
      </div>

      <!-- Total Movements -->
      <div class="pt-4 border-t border-n-weak">
        <div class="flex items-center justify-between text-sm">
          <span class="text-n-slate-11">
            {{ t('PIPELINE.ANALYTICS.PIPELINE_ANALYSIS.TOTAL_MOVEMENTS') }}
          </span>
          <span class="font-medium text-n-slate-12">
            {{ pipelineData.total_movements }}
          </span>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-12">
      <Icon
        icon="i-lucide-database"
        class="size-10 mx-auto mb-2 text-n-slate-9"
      />
      <p class="text-sm text-n-slate-11">
        {{ t('PIPELINE.ANALYTICS.NO_DATA') }}
      </p>
    </div>
  </div>
</template>
